import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  FormTemplate,
  Header5, IfFormData,
  NewLine, RadioButtonOption,
  RadioButtonsGroup,
  SimpleBlock,
  SubmitButton,
  Text,
  TextLink,
  useListContextRequired,
  useMassUpdateServiceRequired,
  usePopupManager,
} from 'ui-builder';
import React from 'react';

import { Popup, PopupText, Separator } from 'features/nekst-widgets';
import {
  AutomaticLinkResultType,
  ContactMatch, ContactSyncDirection,
  ExternalLink,
  LinkAutomaticallyResponse,
  Person,
  useContactLinkService,
  useFubConnectionProfilesService, usePeopleService, useSyncContactService
} from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';

import styles from './PeopleMassLink.module.scss';
import { ChooseDirectionRadioButtons } from '../View/Connections/LinkExistingContactPopup';

type ContactNavigationContextType = {
  openNekstContact: (contactId: number) => void,
  openExternalLink: (url: string) => void,
};

const ContactNavigationReactContext = React.createContext<ContactNavigationContextType | null>(null);

export function ContactNavigationContext(props: {
  children: React.ReactNode,
  value: ContactNavigationContextType,
}) {
  return (
    <ContactNavigationReactContext.Provider value={props.value}>
      {props.children}
    </ContactNavigationReactContext.Provider>
  );
}

function useContactsListUpdater() {
  const peopleService = usePeopleService();

  const listContext = useListContextRequired();


  const updateContacts = async (ids: number[]) => {
      const contacts = await peopleService.getPeople({
        ids,
      }, {
        limit: 50,
        offset: 0
      });

      listContext._updateItems(contacts.data);
  }

  return {
    updateContacts,
  }
}

export function useContactNavigationContext() {
  const context = React.useContext(ContactNavigationReactContext);

  if (!context) {
    throw new Error('useContactNavigationContext must be used within a ContactNavigationProvider');
  }

  return context;
}

function getDescriptionForItem(matches: ContactMatch[]): string {
  return 'The contact was found by: ' + matches.map((match) => {
    switch (match) {
      case ContactMatch.NAME:
        return 'name';
      case ContactMatch.EMAIL:
        return 'email';
      case ContactMatch.PHONE_NUMBER:
        return 'phone number';
      default:
        return match;
    }
  })
    .join('; ');
}

function ChooseFubContactForm(
  props: {
    result: LinkAutomaticallyResponse,
    connectionProfileId: number,
    contactId: number,
    updateResult: (externalLink: ExternalLink) => void,
  }
) {
  const contactLinkService = useContactLinkService();

  const syncContactService = useSyncContactService();

  const options: RadioButtonOption[] = props.result.searchResults.map((item) => {
    return {
      label: `${item.contact.name.firstName} ${item.contact.name.lastName}`,
      value: item.contact.id,
      description: (<>
        {getDescriptionForItem(item.matches)}
        {'. '}
        FUB contact:
        {' '}
        <TextLink href={item.contact._externalProfileUrl} target="_blank">
          {item.contact.name.firstName}
          {' '}
          {item.contact.name.lastName}
        </TextLink>
        .
      </>),
    };
  });

  options.push(({
    label: 'Create New Follow Up Boss Contact',
    value: '',
    description: 'Follow Up Boss will run deduplication process and link to existing contact in its database (if it\'s found), otherwise a new contact entry will be created'
  }));

  const contactsListUpdater = useContactsListUpdater();

  return (
    <FormTemplate<{
      externalContactId: string,
      syncDirection: string,
    }>
      submitFormFunc={async (data) => {
        let person: Person;
        if (data.externalContactId !== '') {
          person = await contactLinkService.linkContact(
            props.contactId,
            {
              externalContactId: data.externalContactId,
              connectionProfileId: props.connectionProfileId,
            }
          );

          if (data.syncDirection === ContactSyncDirection.LOCAL_TO_EXTERNAL) {
            await contactLinkService.updateExternalContact(
              person.id,
              person.externalLinks[0].id
            );
          } else if (data.syncDirection === ContactSyncDirection.EXTERNAL_TO_LOCAL) {
            await contactLinkService.updateNekstContact(
              person.id,
              person.externalLinks[0].id
            );
          }
        } else {
          const result = await syncContactService.createExternalContact(props.contactId, props.connectionProfileId);
          person = result.contact;
        }

        await contactsListUpdater.updateContacts([props.contactId]);

        props.updateResult?.(person.externalLinks[0]);

        return data;
      }}
      initialData={{
        externalContactId: props.result.searchResults[0].contact.id,
        syncDirection: ContactSyncDirection.EXTERNAL_TO_LOCAL
      }}
      buttons={(
        <SubmitButton
          color={ButtonColor.GREEN}
          size={ButtonSize.SMALL}
          variant={ButtonVariant.FILLED}
          text="Link"
        />
      )}
      buttonsBlockClassName={styles.buttonsBlock}
    >
      <RadioButtonsGroup
        options={options}
        source="externalContactId"
        label="Select the correct FUB contact to link"
      />
      <IfFormData conditionFunc={(data) => !!data.externalContactId}>
        <ChooseDirectionRadioButtons source="syncDirection" />
      </IfFormData>
    </FormTemplate>
  );
}

function LinkResultsPopup(
  props: {
    results: Record<number, LinkAutomaticallyResponse>,
    connectionProfileId: number,
  }
) {
  const [results, setResults] = React.useState<Record<number, LinkAutomaticallyResponse>>(props.results);

  const listContext = useListContextRequired<Person>();

  const getNameById = (id: number) => {
    const contact = listContext.data!.find((item) => item.id === id);

    return contact?.profile.name.fullName ?? '';
  };

  const getStatus = (result: LinkAutomaticallyResponse) => {
    switch (result.type) {
      case AutomaticLinkResultType.ALREADY_LINKED:
        return 'Contact was already linked to a FUB contact record - no action done';
      case AutomaticLinkResultType.CANNOT_LINK_AUTOMATICALLY:
        return 'Nekst found possible matches but cannot link automatically - please select the correct FUB contact to link';
      case AutomaticLinkResultType.EXISTING_EXTERNAL_CONTACT_LINKED:
        return 'Contact was linked with existing FUB contact record';
      case AutomaticLinkResultType.NEW_EXTERNAL_CONTACT_CREATED:
        return 'New FUB contact record was created';
    }
  };

  const getExternalContactLabel = (result: LinkAutomaticallyResponse) => {
    let externalContactLabel = result.externalLink?._contactName ?? `${result.externalContact?.name.firstName} ${result.externalContact?.name.lastName}`;

    const email = result.externalContact?.emails[0] || result.externalLink?._contactEmail;

    if (email) {
      externalContactLabel += ` (${email})`;
    }

    return externalContactLabel;
  };

  const getActions = (
    contactId: number,
    result: LinkAutomaticallyResponse,
  ) => {
    switch (result.type) {
      case AutomaticLinkResultType.ALREADY_LINKED:
        return null;
      case AutomaticLinkResultType.EXISTING_EXTERNAL_CONTACT_LINKED:
      case AutomaticLinkResultType.NEW_EXTERNAL_CONTACT_CREATED:
        return (
          <SimpleBlock className={styles.linkedWith}>
            <Text bold>
              Linked with:
            </Text>
            <Text>{' '}</Text>
            <TextLink
              href={result.externalContact?._externalProfileUrl || result.externalLink?._externalProfileUrl}
              target="_blank">
              {getExternalContactLabel(result)}
            </TextLink>
          </SimpleBlock>
        );
      case AutomaticLinkResultType.CANNOT_LINK_AUTOMATICALLY:
        return (
          <SimpleBlock>
            <ChooseFubContactForm
              result={result}
              connectionProfileId={props.connectionProfileId}
              contactId={contactId}
              updateResult={(externalLink) => {
                setResults(
                  (prevState) => ({
                    ...prevState,
                    [contactId]: {
                      ...prevState[contactId],
                      type: AutomaticLinkResultType.EXISTING_EXTERNAL_CONTACT_LINKED,
                      externalLink,
                    },
                  } as Record<number, LinkAutomaticallyResponse>)
                );
              }}
            />
          </SimpleBlock>
        );
    }
  };

  return (
    <Popup
      title="Contacts Linking"
      content={(
        <>
          {Object.entries(results)
            .map(([key, value], index) => {
              const name = getNameById(Number(key));

              return (
                <div key={key}>
                  <Header5 className={styles.contactHeader}>{name}</Header5>
                  <SimpleBlock className={styles.row}>
                    <SimpleBlock className={styles.formHint}>
                      <Text>
                        {getStatus(value)}
                      </Text>
                    </SimpleBlock>
                    {getActions(Number(key), value)}
                  </SimpleBlock>
                  {(index !== Object.keys(results).length - 1) && <Separator />}
                </div>
              );
            })}
        </>
      )}
    />
  );
}

function LinkContactsPopup(props: {
  ids: number[],
  connectionProfileId: number
}) {

  const contactLinkService = useContactLinkService();

  const contactsListUpdater = useContactsListUpdater();

  const popupManager = usePopupManager();


  return (
    <Popup
      title="Link Contacts With FUB Contact Records"
      content={
        <PopupText align="left">
          Once selected, Nekst will perform the following actions:
          <NewLine />
          <NewLine />
          1.The action will only be applied to contacts that are not already linked to a FUB contact
          record.
          <NewLine />
          2. Nekst will attempt to find existing matches in its system using the contact’s name,
          email, and phone number.
          <NewLine />
          3. If the contact matches exactly one FUB record based on name and either email or phone
          number, it will be automatically linked.
          <NewLine />
          4. If no matching FUB record is found, a new FUB contact will be created.
          <NewLine />
          5. If there are multiple possible matches, you will be prompted to select the correct FUB
          contact to link.
        </PopupText>
      }
      isCentered={false}
      buttons={(
        <Button
          onClick={async () => {
            const results = await contactLinkService.batchLinkAutomatically(
              props.ids,
              props.connectionProfileId
            );
            await contactsListUpdater.updateContacts(props.ids);
            popupManager.closePopup();

            setTimeout(() => {
              popupManager.openPopup((
                <LinkResultsPopup
                  results={results}
                  connectionProfileId={props.connectionProfileId}
                />
              ));
            }, 500);

          }}
          text="Sync to FUB"
          color={ButtonColor.GREEN}
        />)}
    />
  );
}

export function usePeopleMassLinkUseCase() {
  const popupManager = usePopupManager();

  const massUpdateService = useMassUpdateServiceRequired();
  const selectedIds = massUpdateService.getCheckedIds();

  const fubConnectionProfileService = useFubConnectionProfilesService();

  const { data: profiles } = useDataLoaderNew(
    fubConnectionProfileService.getProfiles,
  );

  const handleAction = () => {
    popupManager.openPopup((
      <LinkContactsPopup
        ids={selectedIds}
        connectionProfileId={profiles![0].id}
      />
    ));
  };

  const isButtonEnabled = selectedIds.length > 0;

  const isButtonVisible = profiles && profiles.length > 0;

  return {
    handleAction,
    isButtonEnabled,
    isButtonVisible,
  };
}
